import { DEFAULT_BOARD_ID } from '@archon3d/content';
import type { BoardDef } from '@archon3d/core';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { useGameLibrary, refreshGameLibrary, gameDefinition } from '../../editor/gameLibrary';
import {
  DUEL_LEVELS,
  MAX_PLAYERS,
  aiSeat,
  DEFAULT_FIGHTS,
  FIGHTS_MODES,
  controllersEach,
  controlsComplete,
  describeSetup,
  fitSetup,
  humanSeat,
  type FightsMode,
  type GameSetup,
  type SeatDevice,
  type SeatKind,
  type SeatSetup,
} from '../gameSetup';
import { padLabel, sameDevice } from '../../game/controls';
import { useGamepads } from './useGamepads';
import { AI_DEPTHS } from '../settings';
import { BrowserLibraryNotice } from '../../editor/BrowserLibraryNotice';

interface Props {
  /** A game at this device, or a room on the server where friends take the open seats. */
  mode: 'local' | 'host';
  boards: BoardDef[];
  savedBoards: BoardDef[];
  setup: GameSetup;
  onSetup: (setup: GameSetup) => void;
  /** Host mode: which seat the host takes. */
  hostSeat?: number;
  onHostSeat?: (seat: number) => void;
  /** Rows above the board choice, e.g. the host's name. */
  before?: ReactNode;
  /** Opens the board picker; hosting has none (rooms know only the bundled boards). */
  onBoards?: () => void;
  onBack: () => void;
  onStart: () => void;
  startLabel: string;
  startTestId?: string;
  startDisabled?: boolean;
  /** Lines under the summary: the server state, a warning about the game in progress. */
  hints?: string[];
  error?: string | null;
}

/** How captures are settled, each in the plain words a newcomer reads under the choice. */
const FIGHTS_CHOICES: [FightsMode, string, string][] = [
  [
    'card',
    'Card',
    'Card: each fighter comes to the fight with one card of support, chosen in secret; the odds decide, and the cards tip them.',
  ],
  ['roll', 'Roll', 'Roll: the dice decide, weighted by the odds of the two fighters.'],
  ['arena', 'Arena', 'Arena: every capture a person takes part in is fought as a duel, by them.'],
  ['watch', 'Watch', 'Watch: the AI fights every duel in the arena while everyone watches.'],
];

/**
 * One panel for every way a game begins: the board, who takes each side (a person, the AI,
 * or nobody), how strong each AI plays, whether people share this device, how captures are
 * fought, and the smoke for this game. Hosting shows the same choices with "you"
 * and "open" seats instead of people at this device.
 */
export function GameSetupPanel(props: Props) {
  const library = useGameLibrary();
  const [catalogNotice, setCatalogNotice] = useState('');
  const { mode, setup, onSetup } = props;
  const all = [
    ...library.map((g) => g.definition.board),
    ...props.savedBoards.filter((b) => !library.some((g) => g.definition.id === b.id)),
  ];
  const board = all.find((b) => b.id === setup.boardId) ?? props.boards[0]!;
  const fitted = fitSetup(setup, board);
  const humans = fitted.seats.filter((s) => s.kind === 'human').length;
  const pads = useGamepads();
  const controllers = mode === 'local' && controllersEach(fitted);
  const needControls = mode === 'local' && !controlsComplete(fitted);
  const playing = fitted.seats.filter((s) => s.kind !== 'none').length;
  const limits = gameDefinition(board.id)?.players ?? { min: 2, max: board.factions.length };
  const combatRules = gameDefinition(board.id)?.combatRules;
  const allowedFights = combatRules?.modes ?? FIGHTS_MODES;
  const arenaBlocked =
    humans === 0
      ? 'The Arena needs a person to fight: make one side Human.'
      : fitted.oneDevice
        ? 'In the Arena everyone fights their own duel: choose A controller each.'
        : null;
  const hostSeat =
    props.hostSeat ??
    Math.max(
      0,
      fitted.seats.findIndex((s) => s.kind === 'human'),
    );

  const setSeat = (index: number, seat: SeatSetup) =>
    onSetup(
      fitSetup({ ...fitted, seats: fitted.seats.map((s, i) => (i === index ? seat : s)) }, board),
    );
  const setKind = (index: number, kind: SeatKind) => {
    const current = fitted.seats[index]!;
    if (kind === current.kind) return;
    const seat =
      kind === 'human' ? humanSeat() : kind === 'ai' ? aiSeat() : { kind: 'none' as const };
    // A second person joining at this screen takes turns by default; controllers are chosen.
    const secondPerson = kind === 'human' && humans === 1;
    onSetup(
      fitSetup(
        {
          ...fitted,
          seats: fitted.seats.map((s, i) => (i === index ? seat : s)),
          oneDevice: secondPerson ? true : fitted.oneDevice,
        },
        board,
      ),
    );
    if (mode === 'host' && kind !== 'human' && hostSeat === index) {
      const next = fitted.seats.findIndex((s, i) => i !== index && s.kind === 'human');
      props.onHostSeat?.(next >= 0 ? next : index);
    }
  };
  const setDevice = (index: number, device: SeatDevice | undefined) => {
    const seat = fitted.seats[index]!;
    if (seat.kind !== 'human') return;
    setSeat(index, humanSeat(seat.name, device));
  };
  /** The value a controller select shows: the mouse, or a pad by slot and name. */
  const deviceValue = (device: SeatDevice | undefined) =>
    device === undefined ? '' : device === 'mouse' ? 'mouse' : `pad:${device.pad}:${device.id}`;
  const parseDevice = (value: string): SeatDevice | undefined => {
    if (value === 'mouse') return 'mouse';
    const m = /^pad:(\d+):(.*)$/.exec(value);
    return m ? { pad: Number(m[1]), id: m[2]! } : undefined;
  };
  const takenByAnother = (index: number, device: SeatDevice) =>
    fitted.seats.some((s, i) => i !== index && s.kind === 'human' && sameDevice(s.device, device));

  const choose = (index: number, kind: SeatKind, mine: boolean) => {
    if (kind !== 'none' && fitted.seats[index]?.kind === 'none' && playing >= limits.max) return;
    if (mode === 'host' && mine) {
      if (fitted.seats[index]!.kind !== 'human') setSeat(index, humanSeat());
      props.onHostSeat?.(index);
    } else setKind(index, kind);
  };

  return (
    <>
      <h2 className="menu-heading">{mode === 'host' ? 'Host game' : 'New game'}</h2>
      {props.before}
      <div className="menu-row">
        <span className="menu-label">Game</span>
        <select
          className="menu-select"
          value={board.id}
          aria-label="Game"
          data-testid="setup-board"
          onChange={(e) => {
            const next = all.find((b) => b.id === e.target.value) ?? board;
            const nextRules = gameDefinition(next.id)?.combatRules;
            const nextFight = nextRules?.modes.includes(fitted.fights)
              ? fitted.fights
              : (nextRules?.defaultMode ?? DEFAULT_FIGHTS);
            const nextEngagement = nextRules?.engagements.includes(fitted.engagement ?? 'immediate')
              ? (fitted.engagement ?? 'immediate')
              : (nextRules?.defaultEngagement ?? 'immediate');
            const fit = fitSetup(
              { ...fitted, boardId: next.id, fights: nextFight, engagement: nextEngagement },
              next,
            );
            const bound = gameDefinition(next.id)?.players ?? { min: 2, max: next.factions.length };
            fit.seats = fit.seats.map((seat, i) =>
              i >= bound.max
                ? { kind: 'none' }
                : i < bound.min && seat.kind === 'none'
                  ? aiSeat()
                  : seat,
            );
            onSetup(fit);
          }}
        >
          {library.map(({ definition: g, source }) => (
            <option key={g.id} value={g.id}>
              {g.name} · {source} ·{' '}
              {g.players.min === g.players.max
                ? g.players.min
                : `${g.players.min}–${g.players.max}`}{' '}
              players
            </option>
          ))}
          {props.savedBoards
            .filter((b) => !library.some((g) => g.definition.id === b.id))
            .map((b) => (
              <option key={b.id} value={b.id}>
                {b.name} (yours) · 2–{Math.min(b.factions.length, MAX_PLAYERS)} players
              </option>
            ))}
        </select>
        {props.onBoards && <button onClick={props.onBoards}>Boards…</button>}
        <button
          onClick={() =>
            void refreshGameLibrary()
              .then(() => setCatalogNotice('Games refreshed.'))
              .catch((e) => setCatalogNotice(e.message))
          }
        >
          Refresh games
        </button>
      </div>
      {catalogNotice && <p role="status">{catalogNotice}</p>}
      <BrowserLibraryNotice />
      {/* Only the classic board is ranked online: the others travel with their definition. A
          game that ships with RPG-Arena is no custom game (client-tablets-ui:quality#14). */}
      {library.find((g) => g.definition.id === board.id)?.source !== 'Original' ? (
        <p className="hint">
          Custom game · unranked. Published updates do not change a match already started.
        </p>
      ) : (
        mode === 'host' &&
        board.id !== DEFAULT_BOARD_ID && (
          <p className="hint">Unranked: only games on the Classic 9×9 count for the rankings.</p>
        )
      )}
      {board.description && <p className="hint">{board.description}</p>}

      <div className="seats">
        {board.factions.map((faction, i) => {
          const seat = fitted.seats[i]!;
          const mine = mode === 'host' && seat.kind === 'human' && hostSeat === i;
          return (
            <div className="menu-row seat-row" key={faction.id}>
              <span className="menu-label">
                <span className="swatch" style={{ background: faction.color }} />
                {i + 1}. {faction.name}
              </span>
              <div className="choice" role="radiogroup" aria-label={`${faction.name} player`}>
                {mode === 'host' && (
                  <button
                    role="radio"
                    aria-checked={mine}
                    disabled={seat.kind === 'none' && playing >= limits.max}
                    className={mine ? 'active' : ''}
                    onClick={() => choose(i, 'human', true)}
                    data-testid={`seat-${faction.id}-you`}
                  >
                    You
                  </button>
                )}
                <button
                  role="radio"
                  aria-checked={seat.kind === 'human' && !mine}
                  disabled={seat.kind === 'none' && playing >= limits.max}
                  className={seat.kind === 'human' && !mine ? 'active' : ''}
                  onClick={() => choose(i, 'human', false)}
                  data-testid={`seat-${faction.id}-human`}
                >
                  {mode === 'host' ? 'Open' : 'Human'}
                </button>
                <button
                  role="radio"
                  aria-checked={seat.kind === 'ai'}
                  disabled={seat.kind === 'none' && playing >= limits.max}
                  className={seat.kind === 'ai' ? 'active' : ''}
                  onClick={() => choose(i, 'ai', false)}
                  data-testid={`seat-${faction.id}-ai`}
                >
                  AI
                </button>
                <button
                  role="radio"
                  aria-checked={seat.kind === 'none'}
                  className={seat.kind === 'none' ? 'active' : ''}
                  onClick={() => choose(i, 'none', false)}
                  data-testid={`seat-${faction.id}-none`}
                  disabled={seat.kind !== 'none' && playing <= limits.min}
                  title={
                    seat.kind !== 'none' && playing <= limits.min
                      ? `At least ${limits.min} sides play`
                      : undefined
                  }
                >
                  Out
                </button>
              </div>
              {seat.kind === 'human' && mode === 'local' && (
                <input
                  className="name"
                  value={seat.name ?? ''}
                  maxLength={24}
                  placeholder={`Player ${i + 1}`}
                  aria-label={`${faction.name} player's name`}
                  onChange={(e) => setSeat(i, humanSeat(e.target.value, seat.device))}
                />
              )}
              {seat.kind === 'human' && controllers && (
                <select
                  className={`menu-select device${seat.device ? '' : ' missing'}`}
                  value={deviceValue(seat.device)}
                  aria-label={`${faction.name} player's controller`}
                  title="What this player plays with: the mouse and keyboard, or one gamepad"
                  data-testid={`seat-${faction.id}-device`}
                  onChange={(e) => setDevice(i, parseDevice(e.target.value))}
                >
                  <option value="">Controller…</option>
                  <option value="mouse" disabled={takenByAnother(i, 'mouse')}>
                    Mouse & keyboard
                  </option>
                  {pads.map((pad) => (
                    <option
                      key={`${pad.index}:${pad.id}`}
                      value={deviceValue({ pad: pad.index, id: pad.id })}
                      disabled={takenByAnother(i, { pad: pad.index, id: pad.id })}
                    >
                      {padLabel(pad.index, pad.id)}
                      {pad.pressed ? ' ●' : ''}
                    </option>
                  ))}
                  {seat.device &&
                    seat.device !== 'mouse' &&
                    !pads.some(
                      (p) =>
                        p.index === (seat.device as { pad: number }).pad &&
                        p.id === (seat.device as { id: string }).id,
                    ) && (
                      <option value={deviceValue(seat.device)}>
                        {padLabel(seat.device.pad, seat.device.id)} · not connected
                      </option>
                    )}
                </select>
              )}
              {seat.kind === 'ai' && (
                <span className="seat-ai">
                  <select
                    className="menu-select"
                    value={seat.depth}
                    aria-label={`${faction.name} AI thinks`}
                    title="How far the board AI looks ahead"
                    onChange={(e) => setSeat(i, { ...seat, depth: Number(e.target.value) })}
                  >
                    {AI_DEPTHS.map((d) => (
                      <option key={d.depth} value={d.depth}>
                        Board: {d.label}
                      </option>
                    ))}
                  </select>
                  <select
                    className="menu-select"
                    value={seat.duel}
                    aria-label={`${faction.name} AI fights`}
                    title="How sharply it aims and how quickly it moves in the arena"
                    onChange={(e) =>
                      setSeat(i, { ...seat, duel: e.target.value as SeatSetup['duel'] })
                    }
                  >
                    {DUEL_LEVELS.map((d) => (
                      <option key={d.id} value={d.id}>
                        Combat: {d.label}
                      </option>
                    ))}
                  </select>
                </span>
              )}
            </div>
          );
        })}
      </div>
      {/* A first game against a gentle opponent, one tap from any setup (x-player-journey#15). */}
      {mode === 'local' &&
        humans > 0 &&
        fitted.seats.some((s) => s.kind === 'ai' && (s.depth !== 1 || s.duel !== 'novice')) && (
          <button
            className="chip"
            data-testid="setup-first-game"
            onClick={() =>
              onSetup(
                fitSetup(
                  {
                    ...fitted,
                    seats: fitted.seats.map((s) =>
                      s.kind === 'ai' ? { ...s, depth: 1, duel: 'novice' as const } : s,
                    ),
                  },
                  board,
                ),
              )
            }
          >
            First game? A Novice AI: it forgives mistakes
          </button>
        )}

      {mode === 'local' && humans >= 2 && (
        <>
          <div className="menu-row">
            <span className="menu-label">Players</span>
            <div
              className="choice"
              role="radiogroup"
              aria-label="How the players share this computer"
            >
              <button
                role="radio"
                aria-checked={fitted.oneDevice}
                className={fitted.oneDevice ? 'active' : ''}
                data-testid="setup-players-shared"
                title="One device passed around: a phone, a tablet or this screen"
                onClick={() => onSetup(fitSetup({ ...fitted, oneDevice: true }, board))}
              >
                Take turns
              </button>
              <button
                role="radio"
                aria-checked={!fitted.oneDevice}
                className={!fitted.oneDevice ? 'active' : ''}
                data-testid="setup-players-controllers"
                title="Everyone at this computer with a controller of their own"
                onClick={() => onSetup(fitSetup({ ...fitted, oneDevice: false }, board))}
              >
                A controller each
              </button>
            </div>
          </div>
          <p className="hint">
            {fitted.oneDevice
              ? 'The device is handed to whoever is on turn; the AI fights every duel for the people at it (Card, Roll or Watch).'
              : 'Each player picks a controller beside their name: the mouse and keyboard for one, a gamepad for each other. In the Arena both fight their own duel. Press a button on a pad to wake it; the pad with a button down shows ●.'}
          </p>
        </>
      )}
      <div className="menu-row">
        <span className="menu-label">Combats</span>
        <div className="choice" role="radiogroup" aria-label="How combats are fought">
          {FIGHTS_CHOICES.filter(([id]) => allowedFights.includes(id)).map(([id, label, words]) => {
            const disabled = id === 'arena' && arenaBlocked !== null;
            return (
              <button
                key={id}
                role="radio"
                aria-checked={fitted.fights === id}
                className={fitted.fights === id ? 'active' : ''}
                disabled={disabled}
                title={disabled ? arenaBlocked! : words}
                data-testid={`setup-fights-${id}`}
                onClick={() => onSetup(fitSetup({ ...fitted, fights: id }, board))}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>
      {/* The chosen way in plain words, where a phone shows no tooltip (x-player-journey#5); and
          why Arena cannot be chosen, when it cannot (client-tablets-ui:quality#13). */}
      <p className="hint" data-testid="setup-fights-hint">
        {FIGHTS_CHOICES.find(([id]) => id === fitted.fights)?.[2]}
        {arenaBlocked && allowedFights.includes('arena') ? ` ${arenaBlocked}` : ''}
      </p>
      {fitted.fights === 'arena' && (
        <>
          <div className="menu-row">
            <span className="menu-label">Human-friendly AI</span>
            <div className="choice" role="radiogroup" aria-label="Human-friendly AI">
              {[true, false].map((on) => (
                <button
                  key={String(on)}
                  role="radio"
                  aria-checked={(fitted.humanFriendlyAi ?? true) === on}
                  className={(fitted.humanFriendlyAi ?? true) === on ? 'active' : ''}
                  onClick={() => onSetup({ ...fitted, humanFriendlyAi: on })}
                >
                  {on ? 'On' : 'Off'}
                </button>
              ))}
            </div>
          </div>
          <p className="hint">
            Gentler reactions, aim and movement against human Arena players. Character stats,
            AI-versus-AI, Card, Roll and Watch are unchanged. The host chooses for the whole room.
          </p>
        </>
      )}
      {(fitted.fights === 'card' || fitted.fights === 'roll') &&
        (combatRules?.engagements ?? ['immediate']).length > 1 && (
          <div className="menu-row">
            <span className="menu-label">Engagement</span>
            <div className="choice" role="radiogroup" aria-label="When combat starts">
              {(combatRules?.engagements ?? ['immediate']).map((engagement) => (
                <button
                  key={engagement}
                  role="radio"
                  aria-checked={fitted.engagement === engagement}
                  className={fitted.engagement === engagement ? 'active' : ''}
                  onClick={() => onSetup({ ...fitted, engagement })}
                  title={
                    engagement === 'muster'
                      ? 'Other factions with a piece that can reach the contested cell may Join or Pass'
                      : 'Combat starts as soon as an enemy enters an occupied cell'
                  }
                >
                  {engagement === 'muster' ? 'Muster' : 'Immediate'}
                </button>
              ))}
            </div>
          </div>
        )}
      <div className="menu-row">
        <span className="menu-label">Smoke</span>
        <div className="choice" role="radiogroup" aria-label="Smoke over the board">
          {[false, true].map((on) => (
            <button
              key={String(on)}
              role="radio"
              aria-checked={fitted.fog === on}
              className={fitted.fog === on ? 'active' : ''}
              onClick={() => onSetup({ ...fitted, fog: on })}
            >
              {on ? 'On' : 'Off'}
            </button>
          ))}
        </div>
      </div>

      <p className="hint" data-testid="setup-summary">
        {describeSetup(fitted, board)}
        {fitted.oneDevice
          ? ' · taking turns at this screen'
          : controllers
            ? ' · a controller each'
            : ''}
        {mode === 'host' ? ' · open seats nobody takes go to the AI' : ''}
      </p>
      {props.hints?.map((hint) => (
        <p className="hint" key={hint}>
          {hint}
        </p>
      ))}
      {props.error && <p className="hint warn">{props.error}</p>}
      {needControls && (
        <p className="hint warn" data-testid="setup-controls-missing">
          Each player picks a controller before the game starts.
        </p>
      )}
      <div className="menu-actions">
        <button onClick={props.onBack}>Back</button>
        <button
          className="primary"
          onClick={props.onStart}
          disabled={
            props.startDisabled || needControls || playing < limits.min || playing > limits.max
          }
          data-testid={props.startTestId ?? 'menu-start'}
        >
          {props.startLabel}
        </button>
      </div>
    </>
  );
}
